import React, { ChangeEvent, Fragment, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { createNews, deleteNews, getNews, News, updateNews } from 'features/contents/newsAPI';
import { useAlertError } from 'hooks';
import AlertError from 'components/AlertError';
import { getErrorMessage } from 'utils/errors';
import { Link as RRDLink } from 'react-router-dom';

const formatter = new Intl.DateTimeFormat('es-AR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  // eslint-disable-next-line new-cap
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

export default function NewsView() {
  const [news, setNews] = useState<News[]>([]);
  const [currentNews, setCurrentNews] = useState<News>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [showError, messageError, detailsError, callbackError, alert] = useAlertError();

  useEffect(() => {
    getNews()
      .then(news => {
        if (!news || ! Array.isArray(news)) {
          alert(
            'No se pudo recuperar las novedades. Por favor, intente nuevamente más tarde',
            () => {
              setNews([]);
              setCurrentNews(undefined);
              setIsModalOpen(false);
            },
          );
          return;
        }

        setNews(news);
      })
      .catch(err => {
        console.error(err);
        alert(
          'No se pudo recuperar las novedades. Por favor, intente nuevamente más tarde',
          getErrorMessage(err),
          () => {
            setNews([]);
            setCurrentNews(undefined);
            setIsModalOpen(false);
          },
        );
        return;
      });
  }, []);

  const handleEditNewsClick = (id: number) => {
    const newCurrentNews = news.find(link => link.id === id);
    if (!newCurrentNews) {
      setCurrentNews(undefined);
      return;
    }

    setCurrentNews(newCurrentNews);
    setIsModalOpen(true);
  };

  const handleAddNewsClick = () => {
    setCurrentNews({
      id: -1,
      title: '',
      content: '',
      createdAt: '',
      updatedAt: '',
    });

    setIsModalOpen(true);
  };

  const handleRemoveNewsClick = (id: number) => {
    deleteNews(id)
      .then(result => {
        if (!result) {
          return;
        }

        const newNews = news.filter(link => link.id !== id);
        setNews(newNews);
      });
  };

  const handleSaveNewsClick = () => {
    if (!currentNews) {
      return;
    }

    if (currentNews.id == -1) {
      return handleNewsCreation();
    }

    updateNews(currentNews)
      .then(result => {
        if (!result) {
          alert(
            'No se pudo actualizar el contenido de la novedad. Por favor, intente nuevamente más tarde',
            () => {
              setCurrentNews(undefined);
              setIsModalOpen(false);
            },
          );
        }

        const newNews = [...news];
        const idx = newNews.findIndex(current => current.id === currentNews.id);
        newNews[idx] = { ...currentNews };

        setNews(newNews);
        setCurrentNews(undefined);
        setIsModalOpen(false);
      })
      .catch(err => {
        console.error(err);
        alert(
          'No se pudo actualizar el contenido de la novedad. Por favor, intente nuevamente más tarde',
          getErrorMessage(err),
          () => {
            setCurrentNews(undefined);
            setIsModalOpen(false);
          },
        );
      });
  };

  const handleNewsContentChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!currentNews) {
      return;
    }

    setCurrentNews({
      ...currentNews,
      [event.target.name]: event.target.value,
    });
  };

  const handleNewsCreation = () => {
    if (!currentNews || currentNews.id != -1) {
      return;
    }

    createNews(currentNews)
      .then(result => {
        if (!result) {
          alert(
            'No se pudo crear la novedad. Por favor, intente nuevamente más tarde',
            () => {
              setCurrentNews(undefined);
              setIsModalOpen(false);
            },
          );
        }

        const newNews = [...news, currentNews];
        setNews(newNews);
        setCurrentNews(undefined);
        setIsModalOpen(false);
      })
      .catch(err => {
        console.error(err);
        alert(
          'No se pudo crear la novedad. Por favor, intente nuevamente más tarde',
          getErrorMessage(err),
          () => {
            setCurrentNews(undefined);
            setIsModalOpen(false);
          },
        );
      });
  };

  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
          </div>
        </Container>
      </div>
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col md='8'>
                    <h3 className="mb-0">Lista de Novedades</h3>
                  </Col>
                  <Col md='4' className='text-right'>
                    <Button color='primary' className='btn btn-md' onClick={ handleAddNewsClick }>
                      Agregar novedad
                    </Button>
                    <RRDLink to="/admin/contenidos">
                      <Button color='secondary' className='btn btn-md'>
                        Volver
                      </Button>
                    </RRDLink>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {
                  news.length > 0 && (
                    <ListGroup>
                      {
                        news.map(current => (
                          <ListGroupItem key={ current.id }>
                            <div style={{ position: 'absolute', right: '1rem' }}>
                              <Button
                                onClick={ () => handleEditNewsClick(current.id) }
                                color="primary"
                                size="sm"
                                outline
                              ><i className="fa fa-edit"></i></Button>
                              <Button
                                onClick={ () => handleRemoveNewsClick(current.id) }
                                color="warning"
                                size="sm"
                                outline
                              ><i className="fa fa-trash-alt"></i></Button>
                            </div>
                            {
                              current.createdAt
                                ? <>
                                    <small style={{ color: '#8898aa' }}>
                                      Creado: { formatter.format(new Date(current.createdAt)) }
                                    </small>
                                    &nbsp;
                                  </>
                                : null
                            }
                            {
                              current.updatedAt
                                ? <small style={{ color: '#8898aa' }}>
                                    Última actualización: { formatter.format(new Date(current.updatedAt)) }
                                  </small>
                                : null
                            }
                            <h3>{ current.title }</h3>
                            <p>{ current.content.split(/\r?\n/).map((line, idx) => (
                              <Fragment key={idx}>
                                { line }
                                <br />
                              </Fragment>
                            )) }</p>
                          </ListGroupItem>
                        ))
                      }
                    </ListGroup>
                  )
                }
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal isOpen={ isModalOpen } centered toggle={ handleCloseModal } size="lg">
        <ModalHeader toggle={ handleCloseModal }>
          Editar Enlace
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Título</Label>
              <Input
                type="text"
                name="title"
                onChange={ handleNewsContentChange }
                value={ currentNews?.title || '' }
              />
            </FormGroup>
            <FormGroup>
              <Label>Contenido</Label>
              <Input
                type="textarea"
                name="content"
                onChange={ handleNewsContentChange }
                value={ currentNews?.content || '' }
                rows={10}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button onClick={ handleSaveNewsClick } color="primary">
            Guardar
          </Button>
          <Button onClick={ handleCloseModal }>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
      <AlertError isOpen={ showError } message={ messageError } details={ detailsError } onClose={ callbackError } />
    </>
  );
}
